import type { TargetTaxonomyKey } from '../../Generated/TargetTaxonomy.ts'
import { cleanPrimer, maskToIupac, toMasks } from './iupac.ts'
import type { PrimerPair } from './primerTrimDetection.ts'

export type CanonicalPrimerLike = {
  forwardSequence: string
  reverseSequence: string
  amplicon: TargetTaxonomyKey
}

/**
 * One primer pair as the reads see it: two gene-specific sequences, unordered, with every registration
 * that names the same pair folded in. `first`/`second` carry no orientation meaning.
 */
export type KnownPrimerPair = {
  id: string
  first: string
  second: string
  amplicons: TargetTaxonomyKey[]
  /**
   * The registered spellings folded into `first`/`second`, most registered first. `first`/`second` are
   * their union, which no lab writes — the spellings are what an exact primer is written with.
   */
  firstVariants: string[]
  secondVariants: string[]
}

/*
 * Sequencing-adapter overhangs a primer can be registered or entered with. Everything up to and including
 * the last one is stripped: Nextera's mosaic end, and TruSeq's read-primer end (whose final T some
 * registrations drop).
 */
const ADAPTER_ENDS = /^.*(?:AGATGTGTATAAGAGACAG|CTTCCGATCT?)/
const MIN_GENE_BASES = 12
/*
 * Two primers are one when their last bases agree position for position (IUPAC overlap). Only the 3′
 * stretch counts: variants of one primer differ in 5′ extension (MiFish-F vs MiFish-12s-fwd share 18),
 * and detection matches just the 3′ core anyway.
 */
const MIN_SHARED_BASES = 15
const COMPARED_BASES = 18

export const stripAdapter = (primer: string): string => {
  const clean = cleanPrimer(primer)
  const stripped = clean.replace(ADAPTER_ENDS, '')
  return stripped.length >= MIN_GENE_BASES ? stripped : clean
}

export const primersCompatible = (a: string, b: string): boolean => {
  const n = Math.min(a.length, b.length, COMPARED_BASES)
  if (n < MIN_SHARED_BASES) {
    return a === b
  }
  const ma = toMasks(a.slice(-n))
  const mb = toMasks(b.slice(-n))
  return ma.every((mask, i) => (mask & mb[i]) !== 0)
}

/** The longer sequence, with the shared 3′ stretch widened to accept what either accepts. */
const unionPrimer = (a: string, b: string): string => {
  const [long, short] = a.length >= b.length ? [a, b] : [b, a]
  const n = short.length
  const ml = toMasks(long.slice(-n))
  const ms = toMasks(short)
  return long.slice(0, long.length - n) + maskToIupac(ml.map((mask, i) => mask | ms[i]))
}

type Draft = {
  first: string
  second: string
  amplicons: Set<TargetTaxonomyKey>
  firstVariants: Map<string, number>
  secondVariants: Map<string, number>
}

const addVariant = (variants: Map<string, number>, sequence: string) => variants.set(sequence, (variants.get(sequence) ?? 0) + 1)
const byCount = (variants: Map<string, number>): string[] => [...variants].sort((a, b) => b[1] - a[1]).map(([sequence]) => sequence)

/** Matches `pair` against `draft` in either orientation; `swapped` when `pair.forward` is the draft's second. */
const orientation = (draft: Draft, forward: string, reverse: string): 'same' | 'swapped' | null => {
  if (primersCompatible(draft.first, forward) && primersCompatible(draft.second, reverse)) {
    return 'same'
  }
  if (primersCompatible(draft.first, reverse) && primersCompatible(draft.second, forward)) {
    return 'swapped'
  }
  return null
}

/**
 * The registry as distinct pairs: adapter tails stripped, field order ignored, registrations agreeing at
 * the 3′ end merged.
 */
export const normalizePrimerRegistry = (rows: readonly CanonicalPrimerLike[]): KnownPrimerPair[] => {
  const drafts: Draft[] = []
  for (const row of rows) {
    const forward = stripAdapter(row.forwardSequence)
    const reverse = stripAdapter(row.reverseSequence)
    if (forward.length < MIN_GENE_BASES || reverse.length < MIN_GENE_BASES) {
      continue
    }
    const hit = drafts.map((draft) => ({ draft, how: orientation(draft, forward, reverse) })).find((x) => x.how)
    if (!hit) {
      drafts.push({
        first: forward,
        second: reverse,
        amplicons: new Set([row.amplicon]),
        firstVariants: new Map([[forward, 1]]),
        secondVariants: new Map([[reverse, 1]])
      })
      continue
    }
    const [a, b] = hit.how === 'same' ? [forward, reverse] : [reverse, forward]
    hit.draft.first = unionPrimer(hit.draft.first, a)
    hit.draft.second = unionPrimer(hit.draft.second, b)
    hit.draft.amplicons.add(row.amplicon)
    addVariant(hit.draft.firstVariants, a)
    addVariant(hit.draft.secondVariants, b)
  }
  return drafts.map((draft, i) => ({
    id: `pair-${i}`,
    first: draft.first,
    second: draft.second,
    amplicons: [...draft.amplicons].sort(),
    firstVariants: byCount(draft.firstVariants),
    secondVariants: byCount(draft.secondVariants)
  }))
}

export const asPrimerPair = (pair: KnownPrimerPair): PrimerPair => ({ forward: pair.first, reverse: pair.second })

/**
 * The known pair a customer's two primers name — each ends with one side of the pair (tags and adapters
 * may precede it), in either order — or `null` for a pair the registry does not know.
 */
export const findKnownPair = (primers: PrimerPair, registry: readonly KnownPrimerPair[]): KnownPrimerPair | null => {
  const f = stripAdapter(primers.forward)
  const r = stripAdapter(primers.reverse)
  const fits = primersCompatible
  return registry.find((pair) => (fits(f, pair.first) && fits(r, pair.second)) || (fits(f, pair.second) && fits(r, pair.first))) ?? null
}

/** What detection should search the reads for: the known pair's gene sequences, else the entered primers minus adapters. */
export const genePrimersFor = (primers: PrimerPair, registry: readonly KnownPrimerPair[]): PrimerPair => {
  const known = findKnownPair(primers, registry)
  return known ? asPrimerPair(known) : { forward: stripAdapter(primers.forward), reverse: stripAdapter(primers.reverse) }
}
